const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const user = localStorage.getItem('alertmanager_user');
  if (user) {
    try {
      const parsedUser = JSON.parse(user);
      if (parsedUser.token) {
        headers['Authorization'] = `Bearer ${parsedUser.token}`;
      }
    } catch {
      // Ignore parsing errors
    }
  }

  return headers;
};

export interface SystemSetting {
  key: string;
  value: unknown;
  description?: string;
  updatedBy: string;
  updatedAt: string;
}

export interface UserInfo {
  id: string;
  username: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

export const adminAPI = {
  // Get all system settings
  async getSettings(): Promise<SystemSetting[]> {
    const res = await fetch(`/api/auth/admin/settings`, {
      headers: getAuthHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch settings: ${res.status}`);
    }

    const data = await res.json();
    return data.settings;
  },

  // Update system setting
  async updateSystemSetting(key: string, value: unknown, description?: string): Promise<void> {
    const res = await fetch(`/api/auth/admin/settings/${key}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ value, description }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || `Failed to update setting: ${res.status}`);
    }
  },

  // Get all users
  async getUsers(): Promise<UserInfo[]> {
    const res = await fetch(`/api/auth/admin/users`, {
      headers: getAuthHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch users: ${res.status}`);
    }

    const data = await res.json();
    return data.users;
  },

  // Update user username (admin only)
  async updateUsername(userId: string, username: string): Promise<void> {
    const res = await fetch(`/api/auth/admin/users/${userId}/username`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ username }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || `Failed to update username: ${res.status}`);
    }
  },

  // Update user password (admin only)
  async updatePassword(userId: string, password: string): Promise<void> {
    const res = await fetch(`/api/auth/admin/users/${userId}/password`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ password }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || `Failed to update password: ${res.status}`);
    }
  },

  // Create new user (admin only)
  async createUser(userData: { username: string; password: string; role: string }): Promise<unknown> {
    const res = await fetch(`/api/auth/admin/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || `Failed to create user: ${res.status}`);
    }

    return data;
  },

  // Delete user (admin only)
  async deleteUser(userId: string): Promise<void> {
    const res = await fetch(`/api/auth/admin/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || `Failed to delete user: ${res.status}`);
    }
  },
};

export default adminAPI;
